import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { api, apiFileUrl } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { Truck, Plus, CheckCircle2, Camera, XCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { farmDateIso } from "@/lib/farmTime";
import { validateUpload, compressImage } from "@/lib/uploads";

const STATUS_COLOR = {
    scheduled: "bg-blue-500/10 text-blue-700 border-blue-500/30",
    in_transit: "bg-amber-500/10 text-amber-700 border-amber-500/30",
    delivered: "bg-primary/10 text-primary border-primary/30",
    failed: "bg-destructive/10 text-destructive border-destructive/30",
};

export default function DeliveriesPage() {
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [items, setItems] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [dlg, setDlg] = useState(null);
    const [podDlg, setPodDlg] = useState(null);
    const [pod, setPod] = useState({ notes: "", signature: "", photo: null });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [f, d, c, e, inv] = await Promise.all([
            api.get("/farm").then(r => r.data),
            api.get("/deliveries").then(r => r.data),
            api.get("/customers").then(r => r.data),
            api.get("/employees").then(r => r.data).catch(() => []),
            api.get("/invoices").then(r => r.data).catch(() => []),
        ]);
        setFarm(f); setItems(d); setCustomers(c); setEmployees(e); setInvoices(inv);
    };
    useEffect(() => { load(); }, []);
    const cur = (v) => formatCurrency(v, farm.currency);

    const save = async () => {
        const body = {
            ...dlg,
            amount: Number(dlg.amount) || 0,
            status: dlg.status || "scheduled",
        };
        if (dlg.id) await api.put(`/deliveries/${dlg.id}`, body);
        else await api.post("/deliveries", body);
        toast.success(dlg.id ? "Delivery updated" : "Delivery scheduled");
        setDlg(null); load();
    };

    const start = async (d) => {
        await api.post(`/deliveries/${d.id}/mark`, { status: "in_transit" });
        toast.success(`Delivery started for ${d.customer_name}`);
        load();
    };

    const markDelivered = async () => {
        if (!podDlg) return;
        setBusy(true);
        try {
            let photoUrl = "";
            if (pod.photo) {
                const [ok, msg] = validateUpload(pod.photo);
                if (!ok) { toast.error(msg); setBusy(false); return; }
                const compressed = await compressImage(pod.photo);
                // Upload to Drive if connected; fallback to inline base64
                try {
                    const b64 = await new Promise(res => {
                        const r = new FileReader();
                        r.onload = e => res(e.target.result.split(",")[1]);
                        r.readAsDataURL(compressed);
                    });
                    const r = await api.post("/drive/upload-bytes", {
                        filename: `POD-${podDlg.id.slice(0,8)}-${Date.now()}.jpg`,
                        category: "Photos", mime_type: "image/jpeg", data_base64: b64,
                    });
                    photoUrl = r.data.web_view_link || r.data.id;
                } catch {
                    // Preserve proof-of-delivery photos in authenticated application
                    // storage when Drive is unavailable. Browser object URLs expire on
                    // reload and must never be persisted as recovery references.
                    const fd = new FormData();
                    fd.append("file", compressed, `POD-${podDlg.id.slice(0,8)}-${Date.now()}.jpg`);
                    const stored = await api.post("/upload", fd);
                    if (!stored.data?.id) throw new Error("Proof photo storage failed");
                    photoUrl = apiFileUrl(stored.data.id);
                }
            }
            await api.post(`/deliveries/${podDlg.id}/mark`, {
                status: "delivered", pod_photo_url: photoUrl,
                signature: pod.signature, notes: pod.notes,
            });
            toast.success("Delivered ✓");
            setPodDlg(null); setPod({ notes: "", signature: "", photo: null });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to mark delivered");
        } finally { setBusy(false); }
    };

    const markFailed = async (d) => {
        const reason = window.prompt("Reason for failed delivery?");
        if (!reason) return;
        await api.post(`/deliveries/${d.id}/mark`, { status: "failed", notes: reason });
        toast.error(`Delivery failed: ${reason}`);
        load();
    };

    // KPIs
    const pending = items.filter(d => d.status === "scheduled").length;
    const inTransit = items.filter(d => d.status === "in_transit").length;
    const deliveredToday = items.filter(d => d.status === "delivered" && (d.delivered_at || "").slice(0,10) === farmDateIso()).length;
    const failed = items.filter(d => d.status === "failed").length;

    return (
        <div className="space-y-6" data-testid="deliveries-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="tiny-label">{i18n.t("final.7a2c34cb789c")}</div>
                    <h1 className="text-4xl font-bold tracking-tight">{i18n.t("final.bc4f986ecbca")}</h1>
                    <p className="text-muted-foreground mt-1">{i18n.t("final.f5d794b98db8")}</p>
                </div>
                <Dialog open={!!dlg} onOpenChange={o => !o && setDlg(null)}>
                    <DialogTrigger asChild>
                        <Button onClick={() => setDlg({ customer_id: "", customer_name: "", address: "", scheduled_date: farmDateIso(), driver_id: "", amount: "", invoice_id: "", notes: "" })} className="h-11" data-testid="add-delivery-btn">
                            <Plus className="w-4 h-4 mr-1" />{i18n.t("final.cfe7f49e435d")}
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("final.9631af52c66f")}</DialogTitle>
                            <DialogDescription>{i18n.t("final.57328afd89ad")}</DialogDescription>
                        </DialogHeader>
                        {dlg && <div className="space-y-3">
                            <div>
                                <Label className="tiny-label">{i18n.t("final.0e85749a6f40")}</Label>
                                <Select value={dlg.customer_id || "__walkin__"} onValueChange={v => {
                                    if (v === "__walkin__") setDlg({ ...dlg, customer_id: "", customer_name: "" });
                                    else { const c = customers.find(x => x.id === v); setDlg({ ...dlg, customer_id: v, customer_name: c?.name || "", address: c?.address || dlg.address }); }
                                }}>
                                    <SelectTrigger className="h-11 mt-1" data-testid="delivery-customer-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__walkin__">{i18n.t("final.d2b8fb209caf")}</SelectItem>
                                        {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {!dlg.customer_id && <Input placeholder={i18n.t("final.309c693a5ffa")} value={dlg.customer_name} onChange={e => setDlg({ ...dlg, customer_name: e.target.value })} className="h-11 mt-2" />}
                            </div>
                            <div><Label className="tiny-label">{i18n.t("final.d70f93df5e8f")}</Label><Textarea rows={2} value={dlg.address} onChange={e => setDlg({ ...dlg, address: e.target.value })} placeholder={i18n.t("final.a28f0cb43bd1")} data-testid="delivery-address-input" /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label className="tiny-label">{i18n.t("final.19ad699baa33")}</Label><Input type="date" value={dlg.scheduled_date} onChange={e => setDlg({ ...dlg, scheduled_date: e.target.value })} className="h-11 mt-1" data-testid="delivery-date-input" /></div>
                                <div><Label className="tiny-label">{i18n.t("final.52b139c9d30c")}{farm.currency})</Label><Input type="number" value={dlg.amount} onChange={e => setDlg({ ...dlg, amount: e.target.value })} className="h-11 mt-1 font-mono" data-testid="delivery-amount-input" /></div>
                            </div>
                            <div>
                                <Label className="tiny-label">{i18n.t("final.01dbf9f814b1")}</Label>
                                <Select value={dlg.driver_id || "__self__"} onValueChange={v => setDlg({ ...dlg, driver_id: v === "__self__" ? "" : v })}>
                                    <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__self__">{i18n.t("final.7da62f0b3f3f")}</SelectItem>
                                        {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="tiny-label">{i18n.t("final.aef614ca416f")}</Label>
                                <Select value={dlg.invoice_id || "__none__"} onValueChange={v => setDlg({ ...dlg, invoice_id: v === "__none__" ? "" : v })}>
                                    <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">{i18n.t("final.3b906ae77c48")}</SelectItem>
                                        {invoices.slice(0, 50).map(i => <SelectItem key={i.id} value={i.id}>{i.invoice_number || i.id.slice(0,8)} · {cur(i.grand_total || i.total)}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div><Label className="tiny-label">{i18n.t("final.70440046a3dc")}</Label><Textarea rows={2} value={dlg.notes} onChange={e => setDlg({ ...dlg, notes: e.target.value })} placeholder={i18n.t("final.535f9fd79916")} /></div>
                            <Button onClick={save} disabled={!dlg.customer_name || !dlg.address} className="h-11 w-full" data-testid="save-delivery-btn">{i18n.t("final.efc007a393f6")}</Button>
                        </div>}
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-4 bg-blue-500/5"><div className="tiny-label text-blue-700">{i18n.t("final.1cd1bdad468f")}</div><div className="kpi-value text-2xl font-bold mt-2 text-blue-700">{pending}</div></Card>
                <Card className="p-4 bg-amber-500/5"><div className="tiny-label text-amber-700">{i18n.t("final.046e5cee819c")}</div><div className="kpi-value text-2xl font-bold mt-2 text-amber-700">{inTransit}</div></Card>
                <Card className="p-4 bg-primary/5"><div className="tiny-label text-primary">{i18n.t("final.0dff09425a20")}</div><div className="kpi-value text-2xl font-bold mt-2 text-primary">{deliveredToday}</div></Card>
                <Card className={`p-4 ${failed > 0 ? "bg-destructive/5" : ""}`}><div className={`tiny-label ${failed > 0 ? "text-destructive" : ""}`}>{i18n.t("final.09fef5d8d9a3")}</div><div className={`kpi-value text-2xl font-bold mt-2 ${failed > 0 ? "text-destructive" : ""}`}>{failed}</div></Card>
            </div>

            <Card className="p-4">
                <Table>
                    <TableHeader><TableRow>
                        <TableHead>{i18n.t("final.0e85749a6f40")}</TableHead><TableHead>{i18n.t("final.d70f93df5e8f")}</TableHead><TableHead>{i18n.t("final.1cd1bdad468f")}</TableHead>
                        <TableHead className="text-right">{i18n.t("final.43dc8532f7e5")}</TableHead><TableHead>{i18n.t("final.bae7d5be7082")}</TableHead><TableHead />
                    </TableRow></TableHeader>
                    <TableBody>
                        {items.map(d => (
                            <TableRow key={d.id} data-testid={`delivery-row-${d.id}`}>
                                <TableCell><div className="font-semibold">{d.customer_name}</div></TableCell>
                                <TableCell className="text-xs max-w-xs truncate">{d.address}</TableCell>
                                <TableCell className="font-mono text-xs">{d.scheduled_date}</TableCell>
                                <TableCell className="text-right font-mono">{cur(d.amount || 0)}</TableCell>
                                <TableCell><Badge variant="outline" className={`text-xs ${STATUS_COLOR[d.status] || ""}`}>{d.status || "scheduled"}</Badge></TableCell>
                                <TableCell className="text-right space-x-1">
                                    {d.status === "scheduled" && <Button size="sm" variant="outline" onClick={() => start(d)} data-testid={`start-delivery-${d.id}`}><PlayCircle className="w-3 h-3 mr-1" />{i18n.t("final.952f375412e8")}</Button>}
                                    {d.status === "in_transit" && <>
                                        <Button size="sm" onClick={() => setPodDlg(d)} data-testid={`pod-delivery-${d.id}`}><Camera className="w-3 h-3 mr-1" />{i18n.t("final.96bb1ede828d")}</Button>
                                        <Button size="sm" variant="destructive" onClick={() => markFailed(d)}><XCircle className="w-3 h-3" /></Button>
                                    </>}
                                    {(d.status === "delivered" || d.status === "failed") && d.pod_photo_url && <Button size="sm" variant="outline" onClick={() => window.open(d.pod_photo_url, "_blank")}>{i18n.t("final.2dd799d5d755")}</Button>}
                                </TableCell>
                            </TableRow>
                        ))}
                        {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground"><Truck className="w-12 h-12 mx-auto mb-2 opacity-40" />{i18n.t("final.d6941bad8202")}</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </Card>

            {/* POD Dialog */}
            <Dialog open={!!podDlg} onOpenChange={o => !o && setPodDlg(null)}>
                <DialogContent data-testid="pod-dialog">
                    <DialogHeader>
                        <DialogTitle>{i18n.t("final.ddab284f43b2")} {podDlg?.customer_name}</DialogTitle>
                        <DialogDescription>{i18n.t("final.d5cf913dc0e0")}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="tiny-label">{i18n.t("final.7f3cc23a2ff3")}</Label>
                            <Input value={pod.signature} onChange={e => setPod({ ...pod, signature: e.target.value })} placeholder={i18n.t("final.d6fa7b9a44a6")} className="h-11 mt-1" data-testid="pod-signature-input" />
                        </div>
                        <div>
                            <Label className="tiny-label">{i18n.t("final.0f0264a43f32")}</Label>
                            <Input type="file" accept="image/*" capture="environment" onChange={e => setPod({ ...pod, photo: e.target.files?.[0] || null })} className="h-11 mt-1" data-testid="pod-photo-input" />
                            {pod.photo && <div className="text-xs text-muted-foreground mt-1">{pod.photo.name} · {(pod.photo.size/1024/1024).toFixed(1)} {i18n.t("final.6e979f426b67")}</div>}
                        </div>
                        <div>
                            <Label className="tiny-label">{i18n.t("final.70440046a3dc")}</Label>
                            <Textarea rows={2} value={pod.notes} onChange={e => setPod({ ...pod, notes: e.target.value })} placeholder={i18n.t("final.b7873d633d9b")} />
                        </div>
                        <Button onClick={markDelivered} disabled={busy} className="w-full h-12 font-bold" data-testid="confirm-pod-btn">
                            <CheckCircle2 className="w-4 h-4 mr-2" />{busy ? "Uploading..." : "Mark Delivered"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
